import { useState } from 'react';
import { Route, Save, Sparkles } from 'lucide-react';
import { Button, Input, SelectField, SettingsSection } from '@/design-system';
import type { AiSettingsData } from '../../api/ai-settings.service';
import { useAiSettings } from '../../hooks/useAiSettings';
import { SecretSettingsInput } from '../SecretSettingsInput';

const PROVIDERS = [
  { value: 'azure', label: 'Azure AI Services (transcrição rápida)' },
  { value: 'openai', label: 'OpenAI (Whisper)' },
  { value: 'groq', label: 'Groq (Whisper large v3)' },
];

export function AiSection() {
  const { query, save } = useAiSettings();
  const [draft, setDraft] = useState<Partial<AiSettingsData>>({});
  if (query.isPending) return <p role="status" className="text-sm text-gray-500">Carregando configurações de IA…</p>;
  if (!query.data) return <div role="alert" className="space-y-3 text-sm text-red-700">Não foi possível carregar as configurações de IA.
    <Button variant="subtle" onClick={() => void query.refetch()}>Tentar novamente</Button></div>;
  const data = { ...query.data, ...draft };
  const patch = (partial: Partial<AiSettingsData>) => { setDraft((previous) => ({ ...previous, ...partial })); save.reset(); };
  return <div className="space-y-6">
    <header><h2 className="text-xl font-semibold text-gray-900">Inteligência Artificial</h2>
      <p className="mt-1 text-sm text-gray-500">Roteamento de modelos e credenciais dos serviços de IA.</p></header>
    {save.isError && <p role="alert" className="text-sm text-red-700">Não foi possível salvar as configurações de IA.</p>}
    {save.isSuccess && <p role="status" className="text-sm text-emerald-700">Configurações de IA salvas.</p>}
    <fieldset disabled={save.isPending} className="min-w-0 space-y-5">
      <SettingsSection title="Roteamento de modelo (custo × qualidade)" icon={Route}
        description="Configure deployments diferentes para tarefas simples e tarefas complexas.">
        <div className="grid gap-4 pt-5 md:grid-cols-2">
          <div><Input label="Deployment LOW (baixo custo)" value={data.lowTierDeployment ?? ''} placeholder="Nome do deployment"
            onChange={(event) => patch({ lowTierDeployment: event.target.value || null })} />
            <p className="mt-1 text-xs text-gray-500">Para FAQs, áudios transcritos e respostas simples. Vazio = usa o deployment padrão de chat.</p></div>
          <div><Input label="Deployment HIGH (alta qualidade)" value={data.highTierDeployment ?? ''} placeholder="Nome do deployment"
            onChange={(event) => patch({ highTierDeployment: event.target.value || null })} />
            <p className="mt-1 text-xs text-gray-500">Para análise de imagens e documentos longos. Vazio = usa o deployment padrão de chat.</p></div>
        </div>
      </SettingsSection>
      <SettingsSection title="Azure AI Services / IA" icon={Sparkles}
        description="Configure o provedor e as credenciais usados na transcrição de áudios.">
        <div className="space-y-5 pt-5">
          <SelectField label="Provedor de transcrição de voz" options={PROVIDERS} value={data.defaultTranscriptionProvider ?? 'azure'}
            onChange={(value) => patch({ defaultTranscriptionProvider: value })} hint="API usada para converter fala em texto." />
          <div className="grid gap-4 md:grid-cols-3">
            <SecretSettingsInput label="Azure Subscription Key" value={data.azureKey ?? ''} onChange={(value) => patch({ azureKey: value })} />
            <div><Input label="Endpoint Azure" type="url" value={data.azureEndpoint ?? ''} onChange={(event) => patch({ azureEndpoint: event.target.value })} />
              <p className="mt-1 text-xs text-gray-500">Endpoint do recurso em Keys and Endpoint. Quando preenchido, a região pode ficar vazia.</p></div>
            <div><Input label="Região Azure" value={data.azureRegion ?? ''} onChange={(event) => patch({ azureRegion: event.target.value })} />
              <p className="mt-1 text-xs text-gray-500">Obrigatória somente quando o endpoint estiver vazio.</p></div>
          </div>
          <div className="grid gap-4 border-t border-gray-100 pt-5 md:grid-cols-2">
            <SecretSettingsInput label="OpenAI API Key" value={data.openaiApiKey ?? ''} onChange={(value) => patch({ openaiApiKey: value })} />
            <SecretSettingsInput label="Groq API Key" value={data.groqApiKey ?? ''} onChange={(value) => patch({ groqApiKey: value })} />
          </div>
        </div>
      </SettingsSection>
    </fieldset>
    <div className="flex justify-end"><Button isLoading={save.isPending} disabled={!Object.keys(draft).length}
      onClick={() => save.mutate(draft, { onSuccess: () => setDraft({}) })}><Save size={16} />Salvar configurações</Button></div>
  </div>;
}
